<div class="min-h-screen bg-gray-900 text-gray-100 flex flex-col items-center justify-center p-4">
  <div class="bg-gray-800 p-8 rounded-lg shadow-2xl w-full max-w-2xl">
    <h1 class="text-4xl font-bold text-center text-blue-400 mb-8">MP3 Trimmer</h1>

    <!-- File Upload (Primary for Trimming) -->
    <div class="mb-6">
      <label class="block text-sm font-medium text-gray-300 mb-2" for="mp3-upload">Upload MP3 File (for
        trimming)</label>
      <input type="file" id="mp3-upload" accept="audio/mp3,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,audio/m4a,.m4a"
        (change)="handleFileInput($event)"
        class="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 cursor-pointer" />
      @if (filename()) {
      <p class="mt-2 text-sm text-gray-400">Loaded: <span class="text-blue-300">{{ filename() }}</span></p>
      }
    </div>

    @if (errorMessage()) {
    <div class="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded relative mb-6" role="alert">
      <span class="block sm:inline">{{ errorMessage() }}</span>
    </div>
    }

    @if (isLoading()) {
    <div class="flex items-center justify-center space-x-2 text-blue-400 mb-6">
      <div class="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
      <span>Loading audio...</span>
    </div>
    }

    <!-- Onboarding / Instructions Panel -->
    @if (!audioBuffer() && !isLoading()) {
    <div class="bg-gray-700 p-6 rounded-lg mb-6">
      <h2 class="text-2xl font-semibold text-blue-300 mb-4">How to Use Trimmer:</h2>
      <ol class="list-decimal list-inside space-y-2 text-gray-300">
        <li><strong>Load:</strong> Click "Upload MP3 File" to load your audio.</li>
        <li><strong>Play:</strong> Use the Play button to listen to your audio.</li>
        <li><strong>Mark:</strong> Click "Set Start Here" and "Set End Here" to define your trim points.</li>
        <li><strong>Fine-Tune:</strong> Adjust Minutes, Seconds, Milliseconds for precise cuts.</li>
        <li><strong>Preview:</strong> Use "Preview Segment" to listen to your selected portion.</li>
        <li><strong>Save:</strong> Click "Save Trimmed WAV" to download your edited file.</li>
      </ol>
    </div>
    }

    <!-- Audio Player and Controls (Trimming) -->
    @if (audioBuffer()) {
    <div class="mb-8">
      <h2 class="text-2xl font-bold text-center text-blue-400 mb-6">Trimming Controls</h2>
      <div class="flex items-center justify-between mb-4">
        <span class="text-xl font-mono text-gray-300">{{ formatTime(currentTime()) }}</span>
        <span class="text-xl font-mono text-gray-400">/ {{ formatTime(duration()) }}</span>
      </div>

      <!-- Custom Slider with layered visuals -->
      <div class="relative w-full h-2 rounded-lg bg-gray-700 overflow-hidden mb-6">
        <!-- Selected Segment Highlight (Green) -->
        <div class="absolute h-full bg-green-500/30 rounded-lg transition-all duration-75 ease-out"
          [style.left]="(startTime() / duration()) * 100 + '%'"
          [style.width]="((endTime() - startTime()) / duration()) * 100 + '%'"></div>

        <!-- Start Marker (Yellow) -->
        <div class="absolute top-0 h-full w-0.5 bg-yellow-400 rounded-sm -ml-0.5 z-20"
          [style.left]="(startTime() / duration()) * 100 + '%'"></div>

        <!-- End Marker (Yellow) -->
        <div class="absolute top-0 h-full w-0.5 bg-yellow-400 rounded-sm -ml-0.5 z-20"
          [style.left]="(endTime() / duration()) * 100 + '%'"></div>

        <!-- Played Progress Highlight (Blue) -->
        <div class="absolute h-full bg-blue-500 rounded-lg transition-all duration-75 ease-out z-10"
          [style.width]="(currentTime() / duration()) * 100 + '%'"></div>

        <!-- Actual transparent range input for interaction -->
        <input type="range" [value]="currentTime()" [max]="duration()" (input)="seek($event)"
          (mousedown)="isSeeking.set(true)" (mouseup)="isSeeking.set(false); isPlaying() && play()"
          (touchstart)="isSeeking.set(true)" (touchend)="isSeeking.set(false); isPlaying() && play()"
          class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-30" />

        <!-- Custom Thumb for visual feedback -->
        <div
          class="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg border border-gray-300 z-40 transition-transform duration-100 ease-out"
          [class.scale-110]="isSeeking()" [style.left]="(currentTime() / duration()) * 100 + '%'"
          [style.transform]="'translateX(-50%) translateY(-50%)'"></div>
      </div>

      <div class="flex justify-center space-x-4 mb-8">
        <button (click)="!isPlaying() ? play() : pause()" [disabled]="!audioBuffer()"
          class="flex flex-col items-center justify-center p-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-24 h-24">
          <i [attr.data-lucide]="isPlaying() ? 'pause' : 'play'" class="w-6 h-6"></i>
          <span class="text-sm mt-1">{{ isPlaying() ? 'Pause' : 'Play' }}</span>
        </button>
        <button (click)="stop()" [disabled]="!audioBuffer()"
          class="flex flex-col items-center justify-center p-3 rounded-lg bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-24 h-24">
          <i data-lucide="square" class="w-6 h-6"></i>
          <span class="text-sm mt-1">Stop</span>
        </button>
        <button (click)="restart()" [disabled]="!audioBuffer()"
          class="flex flex-col items-center justify-center p-3 rounded-lg bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-24 h-24">
          <i data-lucide="rotate-ccw" class="w-6 h-6"></i>
          <span class="text-sm mt-1">Restart</span>
        </button>
      </div>

      <!-- Trimming Controls -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <!-- Set Start Time -->
        <div class="bg-gray-700 p-4 rounded-lg">
          <h3 class="text-xl font-semibold text-blue-300 mb-3 flex items-center">
            <i data-lucide="arrow-right-circle" class="w-5 h-5 mr-2"></i> Start Time
          </h3>
          <div class="flex justify-between items-center mb-3">
            <span class="text-lg font-mono text-gray-300">{{ formatTime(startTime()) }}</span>
            <button (click)="setStartTime()" [disabled]="!audioBuffer()"
              class="px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              Set Start Here
            </button>
          </div>
          <div class="flex space-x-2">
            <input type="number" min="0" [value]="startMinutes()"
              (input)="startMinutes.set(+$event.target.value); updateStartTimeFromInputs();"
              (blur)="updateStartTimeFromInputs()"
              class="w-1/3 p-2 bg-gray-900 border border-gray-600 rounded-md text-center focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
            <input type="number" min="0" max="59" [value]="startSeconds()"
              (input)="startSeconds.set(+$event.target.value); updateStartTimeFromInputs();"
              (blur)="updateStartTimeFromInputs()"
              class="w-1/3 p-2 bg-gray-900 border border-gray-600 rounded-md text-center focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
            <input type="number" min="0" max="999" [value]="startMilliseconds()"
              (input)="startMilliseconds.set(+$event.target.value); updateStartTimeFromInputs();"
              (blur)="updateStartTimeFromInputs()"
              class="w-1/3 p-2 bg-gray-900 border border-gray-600 rounded-md text-center focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
          </div>
        </div>

        <!-- Set End Time -->
        <div class="bg-gray-700 p-4 rounded-lg">
          <h3 class="text-xl font-semibold text-blue-300 mb-3 flex items-center">
            <i data-lucide="arrow-left-circle" class="w-5 h-5 mr-2"></i> End Time
          </h3>
          <div class="flex justify-between items-center mb-3">
            <span class="text-lg font-mono text-gray-300">{{ formatTime(endTime()) }}</span>
            <button (click)="setEndTime()" [disabled]="!audioBuffer()"
              class="px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              Set End Here
            </button>
          </div>
          <div class="flex space-x-2">
            <input type="number" min="0" [value]="endMinutes()"
              (input)="endMinutes.set(+$event.target.value); updateEndTimeFromInputs();"
              (blur)="updateEndTimeFromInputs()"
              class="w-1/3 p-2 bg-gray-900 border border-gray-600 rounded-md text-center focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
            <input type="number" min="0" max="59" [value]="endSeconds()"
              (input)="endSeconds.set(+$event.target.value); updateEndTimeFromInputs();"
              (blur)="updateEndTimeFromInputs()"
              class="w-1/3 p-2 bg-gray-900 border border-gray-600 rounded-md text-center focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
            <input type="number" min="0" max="999" [value]="endMilliseconds()"
              (input)="endMilliseconds.set(+$event.target.value); updateEndTimeFromInputs();"
              (blur)="updateEndTimeFromInputs()"
              class="w-1/3 p-2 bg-gray-900 border border-gray-600 rounded-md text-center focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
          </div>
        </div>
      </div>

      <!-- Action Buttons (Trimming) -->
      <div class="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
        <button (click)="previewSegment()" [disabled]="!audioBuffer() || startTime() >= endTime()"
          class="flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <i data-lucide="volume-2" class="w-5 h-5 mr-2"></i> Preview Segment
        </button>
        <div class="flex flex-col items-center">
          <button (click)="saveTrimmedAudio()" [disabled]="!audioBuffer() || startTime() >= endTime()"
            class="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <i data-lucide="download" class="w-5 h-5 mr-2"></i> Save Trimmed WAV
          </button>
          <span class="text-xs text-gray-400 mt-1">App Version: {{ version() }}</span>
        </div>
      </div>
    </div>
    }

    <!-- Concatenate Audio Files Section -->
    <div class="mt-12 pt-8 border-t border-gray-700 bg-green-800 px-6 py-6 rounded-lg">
      <h2 class="text-2xl font-bold text-center text-green-100 mb-6">Concatenate Audio Files</h2>

      <!-- First Audio File Upload for Concatenation -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-300 mb-2" for="concat-upload-1">Upload First Audio File for
          Concatenation</label>
        <input type="file" id="concat-upload-1"
          accept="audio/mp3,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,audio/m4a,.m4a"
          (change)="handleFileInputConcat1($event)"
          class="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 cursor-pointer" />
        @if (filenameConcat1()) {
        <p class="mt-2 text-sm text-gray-400">Loaded: <span class="text-blue-300">{{ filenameConcat1() }}</span></p>
        }
      </div>

      <!-- Second Audio File Upload for Concatenation -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-300 mb-2" for="concat-upload-2">Upload Second Audio File for
          Concatenation</label>
        <input type="file" id="concat-upload-2"
          accept="audio/mp3,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,audio/m4a,.m4a"
          (change)="handleFileInputConcat2($event)"
          class="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 cursor-pointer" />
        @if (filenameConcat2()) {
        <p class="mt-2 text-sm text-gray-400">Loaded: <span class="text-blue-300">{{ filenameConcat2() }}</span></p>
        }
      </div>

      @if (concatenateErrorMessage()) {
      <div class="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded relative mb-6" role="alert">
        <span class="block sm:inline">{{ concatenateErrorMessage() }}</span>
      </div>
      }

      @if (isConcatenating()) {
      <div class="flex items-center justify-center space-x-2 text-blue-400 mb-6">
        <div class="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
        <span>Concatenating audio...</span>
      </div>
      }

      <!-- Concatenate Button -->
      <div class="flex justify-center">
        <button (click)="concatenateAudioFiles()"
          [disabled]="!audioBufferConcat1() || !audioBufferConcat2() || isConcatenating()"
          class="flex items-center justify-center px-6 py-3 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <i data-lucide="merge" class="w-5 h-5 mr-2"></i> Concatenate & Save WAV
        </button>
      </div>
    </div>
  </div>
</div>